'use client';

import { useEffect } from 'react';
import { useQueryClient } from 'react-query';

// How long the anchor has to stay put, with no queries in flight, before it is
// considered settled.
const SETTLE_DELAY = 1000;
// Upper bound for following the anchor, so a constantly shifting page can't hold the scroll.
const MAX_FOLLOW_DURATION = 10000;
// Sub-pixel differences from rounding are not a layout shift.
const DRIFT_TOLERANCE = 1;

const USER_SCROLL_EVENTS = [
  'keydown',
  'pointerdown',
  'touchstart',
  'wheel',
] as const;

export const useScrollPersistence = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!('scrollRestoration' in history)) return;

    history.scrollRestoration = 'auto';

    let animationFrameId: number | undefined;

    const getTargetElement = () => {
      const hash = window.location.hash.slice(1);

      if (!hash || hash === 'top') return null;

      const decodedHash = decodeURIComponent(hash);

      return (
        document.getElementById(decodedHash) ||
        document.getElementsByName(decodedHash)[0]
      );
    };

    // `html` has `scroll-behavior: smooth`, and a smooth scroll that is still running
    // when the layout shifts ends up in the wrong place, so corrections jump instantly.
    const scrollToElement = (element: HTMLElement) => {
      element.scrollIntoView({ behavior: 'instant', block: 'start' });

      return element.getBoundingClientRect().top;
    };

    const stopFollowing = () => {
      if (animationFrameId !== undefined)
        cancelAnimationFrame(animationFrameId);

      animationFrameId = undefined;

      USER_SCROLL_EVENTS.forEach(eventName =>
        window.removeEventListener(eventName, stopFollowing),
      );
    };

    // Modules render as skeletons first and grow once their content loads, which pushes
    // the anchored module away from where the browser initially scrolled. The shift can
    // come from any module above it (and `body` has a fixed height, so it never resizes),
    // so the anchor's position is checked every frame and restored whenever it moved.
    // Following stops once it has stayed put with nothing left to fetch, or as soon as
    // the user scrolls on their own.
    const followHash = () => {
      stopFollowing();

      const targetElement = getTargetElement();

      if (!targetElement) return;

      const startedAt = performance.now();
      let lastShiftAt = startedAt;
      let expectedTop = scrollToElement(targetElement);

      const checkPosition = (now: number) => {
        const element = getTargetElement();

        if (!element || now - startedAt > MAX_FOLLOW_DURATION) {
          return stopFollowing();
        }

        const currentTop = element.getBoundingClientRect().top;

        if (Math.abs(currentTop - expectedTop) > DRIFT_TOLERANCE) {
          expectedTop = scrollToElement(element);
          lastShiftAt = now;
        }

        const isSettled =
          now - lastShiftAt > SETTLE_DELAY && queryClient.isFetching() === 0;

        if (isSettled) return stopFollowing();

        animationFrameId = requestAnimationFrame(checkPosition);
      };

      animationFrameId = requestAnimationFrame(checkPosition);

      USER_SCROLL_EVENTS.forEach(eventName =>
        window.addEventListener(eventName, stopFollowing, { passive: true }),
      );
    };

    animationFrameId = requestAnimationFrame(followHash);

    window.addEventListener('hashchange', followHash);

    return () => {
      stopFollowing();
      window.removeEventListener('hashchange', followHash);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
